import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Home, RefreshCw, Users, X, ClipboardCheck } from 'lucide-react';
import { type ModuleData, type ModuleId, getFamilyDefinition } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { DjayRecommendationCard } from './DjayRecommendationCard';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants, containerVariants } from '../shared/animations';
import { AssignedAssessmentBanner } from './AssignedAssessmentBanner';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig, TeacherAssignment } from '../../assessment';
import { DOMAIN_MODULE_MAP } from '../../assessment';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner, type DayActivity } from '../../utils/activityHelpers';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { hasActiveDecay, getWeakestFamiliesGlobal, getDjayRecommendation } from '../../mastery';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { DailyChallengeCard } from './DailyChallengeCard';
import { GlanceBar } from './GlanceBar';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAssignments, setActiveAssignments] = useState<TeacherAssignment[]>([]);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    let cancelled = false;
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        if (!cancelled) setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (cancelled) return;
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      if (!cancelled) setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  useEffect(() => {
    let cancelled = false;
    async function loadAssignments() {
      if (!activeAccount) return;
      const all = await dataLayer.getTeacherAssignments();
      if (cancelled) return;
      const now = new Date();
      const valid = Object.values(all).filter(assignment => {
        if (assignment.group !== selectedGroup) return false;
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      if (!cancelled) setActiveAssignments(valid);
    }
    loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  const level = state.stats.level;
  const xp = state.stats.xp;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  const recommendation = useMemo(() => {
    return getDjayRecommendation(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const recommendedModule = useMemo(() => {
    return recommendation ? modules.find(m => m.id === recommendation.moduleId) : null;
  }, [recommendation, modules]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Find last practiced module
  const lastPracticedModule = useMemo(() => {
    if (!state.questionStates) return null;
    let latestTime = 0;
    let latestModuleId: ModuleId | null = null;
    // We can iterate over modules and find the max lastAttemptAt
    modules.forEach(m => {
      const answered = getAnsweredQuestions(m.id);
      answered.forEach(q => {
        if (q.lastAttemptAt && q.lastAttemptAt > latestTime) {
          latestTime = q.lastAttemptAt;
          latestModuleId = m.id;
        }
      });
    });
    return latestModuleId ? modules.find(m => m.id === latestModuleId) : null;
  }, [modules, getAnsweredQuestions, state.questionStates]);
  // Get decaying families
  const decayingFamilies = useMemo(() => {
    if (!state.familyMastery) return [];
    const weakest = getWeakestFamiliesGlobal(state.familyMastery, selectedGroup, 10);
    return weakest.filter(f => hasActiveDecay(state.familyMastery?.[f.key])).slice(0, 2);
  }, [state.familyMastery, selectedGroup]);
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer data-id="element-1280">
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6 sm:space-y-8" data-id="element-1281">
        <GlanceBar streakDays={currentStreak} dailyProgress={todayQuestionsAnswered} dailyGoalTarget={dailyGoal} level={level} xp={xp} onNavigateToModules={onNavigateToModules} data-id="element-1282" />

        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1283" />
      </motion.div>

      {/* KLASCODE BANNER */}
      {showKlascodeBanner && <motion.div variants={itemVariants} data-id="element-1284">
          <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1285">
            <div className="flex items-center gap-3" data-id="element-1286">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1287">
                <Users className="w-5 h-5" data-id="element-1288" />
              </div>
              <div data-id="element-1289">
                <h3 className="text-sm font-bold text-amber-900" data-id="element-1290">
                  Heb je een klascode?
                </h3>
                <p className="text-xs text-amber-700 font-medium" data-id="element-1291">
                  Je kunt deze op elk moment invullen via Instellingen.
                </p>
              </div>
            </div>
            <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1292">
              <X className="w-4 h-4" data-id="element-1293" />
            </button>
          </Card>
        </motion.div>}

      {/* ACTIVE ASSIGNMENTS */}
      {activeAssignments.length > 0 && <motion.div variants={itemVariants} data-id="element-1294">
          <Card className="p-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1295">
            <div className="flex items-center gap-3" data-id="element-1296">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 shadow-sm" data-id="element-1297">
                <ClipboardCheck className="w-5 h-5" data-id="element-1298" />
              </div>
              <div data-id="element-1299">
                <h3 className="text-sm font-bold text-indigo-900" data-id="element-1300">
                  {activeAssignments.length}{' '}
                  {activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'}{' '}
                  voor je klaar!
                </h3>
                <p className="text-xs text-indigo-700 font-medium" data-id="element-1301">
                  Je leraar heeft nieuwe toetsen klaargezet.
                </p>
              </div>
            </div>
            <Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1302">
              Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1303" />
            </Button>
          </Card>
        </motion.div>}

      {/* QUICK NAVIGATION */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-1304">
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1305">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1306">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1307" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1308">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1309">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1310">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1311" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1312">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1313">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1314">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1315" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1316">
            Toets
          </span>
        </button>
      </motion.div>

      {/* PRIMARY ACTION */}
      <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1317">
        {hasStarted && lastPracticedModule && <ContinueCard module={lastPracticedModule} onStart={() => onStartPractice(lastPracticedModule.id)} djayVariant={selectedVariant} data-id="element-1318" />}
        {hasStarted && recommendedModule && recommendation ? <DjayRecommendationCard module={recommendedModule} recommendation={recommendation} onStart={() => onStartPractice(recommendedModule.id)} djayVariant={selectedVariant} data-id="element-1319" /> : !hasStarted && <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1320" />}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1321">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} onStart={handleStartChallenge} data-id="element-1322" />
        </motion.div>}

      {/* WEEKLY ACTIVITY */}
      <motion.div variants={itemVariants} data-id="element-1323">
        <Card className="p-4 border-2 border-slate-100 bg-white rounded-2xl shadow-sm" data-id="element-1324">
          <div className="flex items-center justify-between mb-3" data-id="element-1325">
            <h3 className="text-sm font-bold text-slate-700" data-id="element-1326">Jouw week</h3>
            <span className="text-xs font-bold text-slate-400" data-id="element-1327">
              Doel: {weeklyGoal} dagen
            </span>
          </div>
          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1328" />
        </Card>
      </motion.div>

      {/* HERHAAL TIP */}
      {decayingFamilies.length > 0 && <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1329">
          {decayingFamilies.map(f => {
        const def = getFamilyDefinition(f.familyId);
        const title = def?.description || f.familyId;
        const module = modules.find(m => f.familyId.includes(m.id));
        return <Card key={f.familyId} className="p-4 sm:p-5 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl cursor-pointer hover:shadow-lg transition-all group shadow-md" onClick={() => module && onStartPractice(module.id)} data-id="element-1330">
                <div className="flex items-center gap-3 sm:gap-4" data-id="element-1331">
                  <div className="flex-shrink-0" data-id="element-1332">
                    <DjayAvatar size="xs" emotion="thinking" variant={selectedVariant} animate data-id="element-1333" />
                  </div>
                  <div className="flex-1" data-id="element-1334">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1" data-id="element-1335">
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" data-id="element-1336" />
                      <h3 className="text-sm sm:text-base font-black text-teal-900" data-id="element-1337">
                        Tijd voor een opfrisser!
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-teal-700 font-medium leading-relaxed" data-id="element-1338">
                      Je hebt <strong className="text-teal-900" data-id="element-1339">{title}</strong>{' '}
                      al een tijdje niet geoefend.
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 group-hover:bg-teal-500 group-hover:text-white transition-colors flex-shrink-0 shadow-sm" data-id="element-1340">
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1341" />
                  </div>
                </div>
              </Card>;
      })}
        </motion.div>}
    </PageContainer>;
}
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1342">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1343" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1344">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1345">
          <div className="block sm:hidden" data-id="element-1346">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} animate data-id="element-1347" />
          </div>
          <div className="hidden sm:block" data-id="element-1348">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1349" />
          </div>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1350">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1351">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1352">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1353" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}
function ContinueCard({
  module,
  onStart,
  djayVariant
}: {
  module: ModuleData;
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  const colorMap: Record<string, string> = {
    'text-blue-500': 'from-blue-50 to-indigo-50 border-blue-200 text-blue-900 bg-blue-600 hover:bg-blue-700',
    'text-indigo-500': 'from-indigo-50 to-violet-50 border-indigo-200 text-indigo-900 bg-indigo-600 hover:bg-indigo-700',
    'text-orange-500': 'from-orange-50 to-amber-50 border-orange-200 text-orange-900 bg-orange-600 hover:bg-orange-700',
    'text-sky-500': 'from-sky-50 to-blue-50 border-sky-200 text-sky-900 bg-sky-600 hover:bg-sky-700',
    'text-green-500': 'from-green-50 to-emerald-50 border-green-200 text-green-900 bg-green-600 hover:bg-green-700',
    'text-rose-500': 'from-rose-50 to-pink-50 border-rose-200 text-rose-900 bg-rose-600 hover:bg-rose-700',
    'text-amber-500': 'from-amber-50 to-yellow-50 border-amber-200 text-amber-900 bg-amber-600 hover:bg-amber-700',
    'text-purple-500': 'from-purple-50 to-fuchsia-50 border-purple-200 text-purple-900 bg-purple-600 hover:bg-purple-700',
    'text-teal-500': 'from-teal-50 to-emerald-50 border-teal-200 text-teal-900 bg-teal-600 hover:bg-teal-700',
    'text-cyan-500': 'from-cyan-50 to-sky-50 border-cyan-200 text-cyan-900 bg-cyan-600 hover:bg-cyan-700'
  };
  const theme = colorMap[module.color] || colorMap['text-blue-500'];
  const [bgGradient, border, textColor, btnBg, btnHover] = theme.split(' ').reduce((acc, cls) => {
    if (cls.startsWith('from-')) acc[0] = `${cls} ${theme.split(' ')[1]}`;
    if (cls.startsWith('border-')) acc[1] = cls;
    if (cls.startsWith('text-')) acc[2] = cls;
    if (cls.startsWith('bg-')) acc[3] = cls;
    if (cls.startsWith('hover:bg-')) acc[4] = cls;
    return acc;
  }, ['', '', '', '', '']);
  return <Card className={`p-4 sm:p-6 border-2 ${border} bg-gradient-to-r ${bgGradient} rounded-2xl shadow-md relative overflow-hidden group cursor-pointer`} onClick={onStart} data-id="element-1354">
      <div className="absolute right-0 top-0 w-32 h-32 bg-white/40 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1355" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10" data-id="element-1356">
        <div className="flex items-center gap-4" data-id="element-1357">
          <div className="flex-shrink-0" data-id="element-1358">
            <DjayAvatar size="sm" emotion="happy" variant={djayVariant} animate data-id="element-1359" />
          </div>
          <div data-id="element-1360">
            <h3 className={`text-xs font-bold ${textColor} uppercase tracking-wider mb-1 opacity-80`} data-id="element-1361">
              Ga verder waar je gebleven was
            </h3>
            <p className={`text-xl font-black ${textColor}`} data-id="element-1362">{module.title}</p>
            <div className="flex items-center gap-2 mt-2" data-id="element-1363">
              <div className="h-1.5 w-24 bg-white/60 rounded-full overflow-hidden" data-id="element-1364">
                <div className={`h-full ${btnBg} rounded-full w-2/3`} data-id="element-1365" />
              </div>
              <span className={`text-xs font-bold ${textColor} opacity-70`} data-id="element-1366">
                Lekker bezig!
              </span>
            </div>
          </div>
        </div>
        <Button onClick={e => {
        e.stopPropagation();
        onStart();
      }} size="lg" className={`${btnBg} ${btnHover} text-white shadow-sm rounded-xl w-full sm:w-auto transition-transform group-hover:scale-105`} data-id="element-1367">
          Ga verder <ArrowRight className="w-5 h-5 ml-2" data-id="element-1368" />
        </Button>
      </div>
    </Card>;
}
function DailyGoalCard({
  answered,
  goal
}: {
  answered: number;
  goal: number;
}) {
  const percentage = Math.min(100, Math.round(answered / goal * 100));
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - percentage / 100 * circumference;
  let message = 'Begin je avontuur!';
  if (percentage >= 100) message = 'Dagdoel gehaald! 🎉';else if (percentage >= 50) message = 'Bijna daar!';else if (percentage > 0) message = 'Goed bezig!';
  return <Card className="p-6 border-2 border-amber-100 bg-white rounded-[2rem] flex flex-col items-center justify-center text-center relative overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-1369">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-50 rounded-full blur-2xl" data-id="element-1370" />

      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 relative z-10" data-id="element-1371">
        Dagdoel
      </h3>

      <div className="relative mb-4" style={{
      width: size,
      height: size
    }} data-id="element-1372">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90" data-id="element-1373">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#FEF3C7" strokeWidth={strokeWidth} data-id="element-1374" />
          <motion.circle initial={{
          strokeDashoffset: circumference
        }} animate={{
          strokeDashoffset: offset
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={percentage >= 100 ? '#10B981' : '#F59E0B'} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeLinecap="round" data-id="element-1375" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1376">
          <span className="text-2xl font-black text-slate-800 leading-none" data-id="element-1377">
            {answered}
          </span>
          <span className="text-xs font-bold text-slate-400 border-t-2 border-slate-100 pt-0.5 mt-0.5 w-8 text-center" data-id="element-1378">
            {goal}
          </span>
        </div>
      </div>

      <p className={`font-bold ${percentage >= 100 ? 'text-emerald-600' : 'text-amber-600'} relative z-10`} data-id="element-1379">
        {message}
      </p>
    </Card>;
}
function WeekStreakCard({
  streak,
  weeklyActivity,
  weeklyGoal
}: {
  streak: number;
  weeklyActivity: DayActivity[];
  weeklyGoal: number;
}) {
  return <Card className="p-6 border-2 border-orange-100 bg-white rounded-[2rem] flex flex-col justify-between relative overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-1380">
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-50 rounded-full blur-2xl" data-id="element-1381" />

      <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-1382">
        <div data-id="element-1383">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-1384">
            Weekdoel
          </h3>
          <div className="flex items-baseline gap-2" data-id="element-1385">
            <span className="text-4xl font-black text-slate-800" data-id="element-1386">{streak}</span>
            <span className="text-sm font-bold text-slate-500" data-id="element-1387">
              / {weeklyGoal} dagen
            </span>
          </div>
        </div>
        <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center shadow-sm" data-id="element-1388">
          <StreakFlame streak={streak} size="md" data-id="element-1389" />
        </div>
      </div>

      <div className="relative z-10" data-id="element-1390">
        <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1391" />
      </div>
    </Card>;
}